import { useEffect, useMemo, useRef, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { getMyAthleteNumberConfig } from "@/lib/api/training-center.functions";
import { DEFAULT_ATHLETE_NUMBER_CONFIG, validateAthleteNumber, type AthleteNumberConfig } from "@/lib/athlete-number";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import { categoryFromBirth } from "@/lib/category";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { cropPhotoFromDocument } from "@/lib/document-photo";
import { rotateImageFromUrl } from "@/lib/image-rotate";

import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

import {
  Users, Plus, Search, Trash2, Edit, Upload, Loader2, ChevronRight, PauseCircle, UserCog,
  ScanLine, UserPlus2, X, Heart, MessageCircle, KeyRound, Copy, Send, FileUp, RotateCw,
} from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { PlayerAvatar, PLAYER_POSES } from "@/components/shared/PlayerAvatar";
import { SkinEditor } from "@/components/shared/SkinEditor";
import { listStudents, listParents, upsertStudent, confirmStudentEnrollment } from "@/lib/api/students.functions";
import { RemoveStudentDialog } from "@/components/students/RemoveStudentDialog";
import { ServiceContractDialog } from "@/components/contracts/ServiceContractDialog";
import { SuspendStudentDialog } from "@/components/students/SuspendStudentDialog";
import { isSuspended } from "@/lib/suspension";
import { DebtWarning } from "@/components/students/DebtWarning";
import { ImportStudentsCsvDialog } from "@/components/students/ImportStudentsCsvDialog";
import { SignupLinkDialog } from "@/components/students/SignupLinkDialog";
import { StudentWhatsappDialog } from "@/components/students/StudentWhatsappDialog";
import { createParentInvite, sendInviteWhatsapp } from "@/lib/api/parent-invites.functions";





import { extractDocument } from "@/lib/api/document-ocr.functions";
import { lookupCep, maskCep } from "@/lib/viacep";


import { maskCNPJ, padCnpj } from "@/lib/doc-match";

const maskCpf = (v: string) =>

  v.replace(/\D/g, "").slice(0, 11)
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d{1,2})$/, "$1-$2");

const maskPhone = (v: string) => {
  const d = v.replace(/\D/g, "").slice(0, 11);
  if (d.length <= 10) return d.replace(/(\d{2})(\d{0,4})(\d{0,4})/, (_m, a, b, c) => {
    let s = "";
    if (a) s += `(${a}`;
    if (a && a.length === 2) s += ") ";
    if (b) s += b;
    if (c) s += `-${c}`;
    return s;
  }).trim();
  return d.replace(/(\d{2})(\d{5})(\d{0,4})/, "($1) $2-$3").trim();
};

function isValidCpf(raw: string): boolean {
  const cpf = (raw || "").replace(/\D/g, "");
  if (cpf.length !== 11) return false;
  if (/^(\d)\1{10}$/.test(cpf)) return false;
  let sum = 0;
  for (let i = 0; i < 9; i++) sum += parseInt(cpf[i], 10) * (10 - i);
  let d1 = (sum * 10) % 11;
  if (d1 === 10) d1 = 0;
  if (d1 !== parseInt(cpf[9], 10)) return false;
  sum = 0;
  for (let i = 0; i < 10; i++) sum += parseInt(cpf[i], 10) * (11 - i);
  let d2 = (sum * 10) % 11;
  if (d2 === 10) d2 = 0;
  return d2 === parseInt(cpf[10], 10);
}

export const Route = createFileRoute("/_authenticated/m/alunos")({
  component: AlunosPage,
});

type Tournament = { name: string; position?: string | null; notes?: string | null };
type ParentItem = {
  id: string; name: string; cpf: string | null; birth_date?: string | null; phone: string | null; email: string | null;
  cep?: string | null; street?: string | null; number?: string | null; complement?: string | null;
  neighborhood?: string | null; city?: string | null; state?: string | null;
  user_id?: string | null;
};

type StudentSchedule = {
  id: string; name: string; category: string | null;
  day_of_week: number | null; start_time: string | null; end_time: string | null;
  location: string | null;
};

type Student = {
  id: string; name: string; cpf: string | null; birth_date: string | null;
  category: string | null; weight: number | null; height: number | null;
  status: string; photo_url: string | null; payment_status: string | null;
  document_type: string | null; document_front_url: string | null; document_back_url: string | null;
  youtube_url: string | null; tournaments: Tournament[]; avatar_config: any;
  jersey_number: string | null; jersey_name: string | null;
  has_health_plan?: boolean | null;
  health_plan_name?: string | null;
  health_plan_contact?: string | null;
  suspended_from?: string | null;
  suspended_until?: string | null;
  suspension_reason?: string | null;
  created_via?: string | null;
  enrollment_confirmed_at?: string | null;
  schedules?: StudentSchedule[];
  parents: {
    id: string; name: string; phone: string | null; email: string | null;
    cpf?: string | null; birth_date?: string | null; cep?: string | null; street?: string | null;
    number?: string | null; complement?: string | null; neighborhood?: string | null;
    city?: string | null; state?: string | null; user_id?: string | null;
  } | null;
  plan_start_date?: string | null;
  plan_end_date?: string | null;
  plan_prices?: { id: string; plan_type: string | null; price: number | null; validity: string | null } | null;
  latest_contract?: {
    id: string; sign_date: string | null; status: string | null;
    plan_snapshot: { plan_type?: string; validity?: string; price?: number } | null;
  } | null;
};

const DAY_LABELS = ["Domingo", "Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado"];

function scheduleLabel(sc: StudentSchedule) {
  const day = sc.day_of_week != null ? DAY_LABELS[sc.day_of_week] ?? "" : "";
  const time = sc.start_time && sc.end_time
    ? `${sc.start_time.slice(0, 5)} às ${sc.end_time.slice(0, 5)}`
    : "";
  return [sc.name, day, time, sc.location ? `(${sc.location})` : ""].filter(Boolean).join(" • ");
}

function isLinkStudent(s: Student) {
  return (s.created_via ?? "ct") === "link";
}

function hasPlan(s: Student) {
  return !!(s.plan_prices || s.latest_contract);
}

function hasSchedule(s: Student) {
  return (s.schedules?.length ?? 0) > 0;
}

function isPendingLink(s: Student) {
  return isLinkStudent(s) && !s.enrollment_confirmed_at;
}


function fmtDateBr(d: string | null) {
  if (!d) return "—";
  const y = d.slice(0, 4);
  return y;
}

function fmtFullBr(d: string | null | undefined) {
  if (!d) return "—";
  const [y, m, day] = d.slice(0, 10).split("-");
  if (!y || !m || !day) return d;
  return `${day}/${m}/${y}`;
}

function formatContractPeriod(signDate: string | null | undefined, validity: string | null | undefined) {
  if (!signDate) return "—";
  const start = new Date(signDate + "T00:00:00");
  if (Number.isNaN(start.getTime())) return fmtFullBr(signDate);
  const v = (validity ?? "").toLowerCase();
  const monthsMap: Record<string, number> = {
    mensal: 1, bimestral: 2, trimestral: 3, semestral: 6, anual: 12, bianual: 24,
  };
  const months = monthsMap[v];
  if (!months) return `desde ${fmtFullBr(signDate)}`;
  const end = new Date(start);
  end.setMonth(end.getMonth() + months);
  end.setDate(end.getDate() - 1);
  return `${fmtFullBr(signDate)} → ${fmtFullBr(end.toISOString().slice(0, 10))}`;
}

function AlunosPage() {
  const list = useServerFn(listStudents);
  const listP = useServerFn(listParents);
  const save = useServerFn(upsertStudent);

  const [loading, setLoading] = useState(true);
  const [students, setStudents] = useState<Student[]>([]);
  const [parents, setParents] = useState<ParentItem[]>([]);
  const [tcId, setTcId] = useState<string | null>(null);


  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("ativo");
  const [paymentFilter, setPaymentFilter] = useState<string>("all");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [enrollFilter, setEnrollFilter] = useState<string>("all");
  const [pendFilter, setPendFilter] = useState<string>("all");
  const [parentFilter, setParentFilter] = useState<string>("all");


  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Student | null>(null);
  const [contractStudent, setContractStudent] = useState<Student | null>(null);

  const reload = async () => {
    setLoading(true);
    try {
      const [s, p] = await Promise.all([list(), listP()]);
      setStudents((s.students ?? []) as unknown as Student[]);
      setParents((p.parents ?? []) as ParentItem[]);
      setTcId((s as any).tcId ?? null);
    } catch (e: any) {
      toast.error(e?.message || "Erro ao carregar");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, []);


  const categories = useMemo(() => {
    const set = new Set<string>();
    students.forEach((s) => { if (s.category) set.add(s.category); });
    return Array.from(set).sort();
  }, [students]);

  // Base: todos os filtros exceto categoria (usada para os contadores)
  const normStatus = (v: string | null | undefined) =>
    v === "active" ? "ativo" : v === "inactive" ? "inativo" : (v ?? "ativo");

  const baseFiltered = useMemo(() => {
    return students.filter((s) => {
      if (search) {
        const q = search.toLowerCase();
        const respName = (s.parents?.name ?? "").toLowerCase();
        if (!s.name.toLowerCase().includes(q) && !respName.includes(q)) return false;
      }
      if (parentFilter === "__none__") {
        if (s.parents?.id) return false;
      } else if (parentFilter !== "all" && (s.parents?.id ?? "") !== parentFilter) {
        return false;
      }
      if (statusFilter === "afastado") {
        if (!isSuspended(s)) return false;
      } else if (statusFilter !== "all") {
        if (normStatus(s.status) !== statusFilter) return false;
        if (statusFilter === "ativo" && isSuspended(s)) return false;
      }
      if (paymentFilter !== "all" && (s.payment_status ?? "pendente") !== paymentFilter) return false;
      if (enrollFilter === "confirmada" && !s.enrollment_confirmed_at) return false;
      if (enrollFilter === "confirmar" && !!s.enrollment_confirmed_at) return false;
      return true;
    });
  }, [students, search, parentFilter, statusFilter, paymentFilter, enrollFilter]);

  const parentOptions = useMemo(() => {
    const map = new Map<string, string>();
    students.forEach((s) => {
      if (s.parents?.id) map.set(s.parents.id, s.parents.name ?? "Responsável");
    });
    return Array.from(map.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [students]);



  const categoryCounts = useMemo(() => {
    const map = new Map<string, number>();
    baseFiltered.forEach((s) => {
      const key = s.category || "__none__";
      map.set(key, (map.get(key) ?? 0) + 1);
    });
    const subNum = (c: string) => {
      const m = c.match(/(\d+)/);
      return m ? Number(m[1]) : 9999;
    };
    return Array.from(map.entries())
      .sort((a, b) => {
        if (a[0] === "__none__") return 1;
        if (b[0] === "__none__") return -1;
        const d = subNum(a[0]) - subNum(b[0]);
        return d !== 0 ? d : a[0].localeCompare(b[0]);
      })
      .map(([key, count]) => ({
        key,
        label: key === "__none__" ? "Sem categoria" : key,
        count,
      }));
  }, [baseFiltered]);

  const filtered = useMemo(() => {
    let arr = baseFiltered;
    if (categoryFilter === "__none__") arr = baseFiltered.filter((s) => !s.category);
    else if (categoryFilter !== "all") arr = baseFiltered.filter((s) => (s.category ?? "") === categoryFilter);
    if (pendFilter === "sem_plano") arr = arr.filter((s) => !hasPlan(s));
    else if (pendFilter === "sem_horario") arr = arr.filter((s) => !hasSchedule(s));
    // Alunos cadastrados pelo link e ainda sem confirmação vêm sempre primeiro
    return [...arr].sort((a, b) => Number(isPendingLink(b)) - Number(isPendingLink(a)));
  }, [baseFiltered, categoryFilter, pendFilter]);


  const pendingLink = useMemo(() => students.filter(isPendingLink), [students]);

  const openNew = () => { setEditing(null); setModalOpen(true); };
  const [modalTab, setModalTab] = useState<"aluno" | "responsavel">("aluno");
  const openEdit = (s: Student, tab: "aluno" | "responsavel" = "aluno") => {
    setEditing(s); setModalTab(tab); setModalOpen(true);
  };

  const [removing, setRemoving] = useState<Student | null>(null);
  const [suspending, setSuspending] = useState<Student | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [signupOpen, setSignupOpen] = useState(false);
  const [waTarget, setWaTarget] = useState<{ student: Student; message: string; title: string; confirm?: boolean } | null>(null);
  const confirmEnrollment = useServerFn(confirmStudentEnrollment);

  // Auto-confirmação: aluno com plano de pagamento E horário de treino é confirmado automaticamente.
  const autoDone = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (loading) return;
    const targets = students.filter(
      (s) => !s.enrollment_confirmed_at && hasPlan(s) && hasSchedule(s) && !autoDone.current.has(s.id),
    );
    if (targets.length === 0) return;
    targets.forEach((s) => autoDone.current.add(s.id));
    (async () => {
      let ok = 0;
      for (const s of targets) {
        try {
          await confirmEnrollment({ data: { studentId: s.id } });
          ok++;
        } catch { /* ignora falhas individuais */ }
      }
      if (ok > 0) {
        toast.success(`${ok} matrícula(s) confirmada(s) automaticamente (plano + turma).`);
        reload();
      }
    })();
  }, [students, loading]);



  const openFreeWhatsapp = (s: Student) => {
    setWaTarget({
      student: s,
      title: `Mensagem para ${s.parents?.name ?? s.name}`,
      message: `Olá ${s.parents?.name ?? ""}! `,
    });
  };

  const openConfirmEnrollment = (s: Student) => {
    const sc = (s.schedules ?? []).map((x) => `• ${scheduleLabel(x)}`).join("\n");
    const plan = s.latest_contract?.plan_snapshot?.plan_type ?? s.plan_prices?.plan_type ?? "—";
    setWaTarget({
      student: s,
      confirm: true,
      title: `Confirmar matrícula de ${s.name}`,
      message:
        `Olá ${s.parents?.name ?? ""}! A matrícula do(a) atleta ${s.name} foi ACEITA. 🎉\n\n` +
        `Plano: ${plan}\n` +
        `Turma e horário:\n${sc || "—"}\n\n` +
        `Qualquer dúvida estamos à disposição. Seja bem-vindo(a)!`,
    });
  };


  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Users className="w-6 h-6 text-primary" /> Alunos
          </h1>
          <p className="text-muted-foreground text-sm">Galeria para exibição dos alunos matriculados.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={() => setSignupOpen(true)}>
            <KeyRound className="w-4 h-4 mr-2" /> Link de cadastro
          </Button>
          <Button variant="outline" onClick={() => setImportOpen(true)}>
            <FileUp className="w-4 h-4 mr-2" /> Importar CSV
          </Button>
          <Button onClick={openNew}>
            <Plus className="w-4 h-4 mr-2" /> Cadastrar Aluno
          </Button>
        </div>
      </div>

      {!loading && pendingLink.length > 0 && (
        <div className="rounded-xl border-2 border-red-500 bg-red-50 dark:bg-red-950/30 p-4 space-y-1">
          <p className="font-semibold text-red-700 dark:text-red-300 flex items-center gap-2">
            <UserPlus2 className="w-4 h-4" />
            {pendingLink.length} aluno(s) cadastrado(s) pelo link de cadastro
          </p>
          <p className="text-sm text-red-700/90 dark:text-red-300/90">
            É necessário matricular em uma turma (horário de treino) e inserir o plano de pagamento.
            Depois disso, use o botão “Confirmar matrícula” no card do aluno.
          </p>
          <p className="text-xs text-red-700/80 dark:text-red-300/80">
            Pendentes: {pendingLink.map((s) => s.name).join(", ")}
          </p>
        </div>
      )}

      <SignupLinkDialog open={signupOpen} onOpenChange={setSignupOpen} />



      <ImportStudentsCsvDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        onImported={() => reload()}
      />


      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input className="pl-9" placeholder="Buscar aluno por nome..." value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-[150px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os status</SelectItem>
            <SelectItem value="ativo">Ativo</SelectItem>
            <SelectItem value="inativo">Inativo</SelectItem>
            <SelectItem value="afastado">Afastado</SelectItem>
          </SelectContent>
        </Select>
        <Select value={paymentFilter} onValueChange={setPaymentFilter}>
          <SelectTrigger className="w-full sm:w-[170px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos pagamentos</SelectItem>
            <SelectItem value="pago">Pago</SelectItem>
            <SelectItem value="pendente">Pendente</SelectItem>
            <SelectItem value="atrasado">Atrasado</SelectItem>
          </SelectContent>
        </Select>
        <Select value={enrollFilter} onValueChange={setEnrollFilter}>
          <SelectTrigger className="w-full sm:w-[190px]"><SelectValue placeholder="Matrícula" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas as matrículas</SelectItem>
            <SelectItem value="confirmar">Confirmar matrícula</SelectItem>
            <SelectItem value="confirmada">Matrícula confirmada</SelectItem>
          </SelectContent>
        </Select>
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-full sm:w-[170px]"><SelectValue placeholder="Categoria" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas categorias</SelectItem>
            {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            <SelectItem value="__none__">Sem categoria</SelectItem>
          </SelectContent>
        </Select>
        <Select value={parentFilter} onValueChange={setParentFilter}>
          <SelectTrigger className="w-full sm:w-[210px]"><SelectValue placeholder="Responsável" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os responsáveis</SelectItem>
            {parentOptions.map((p) => (
              <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
            ))}
            <SelectItem value="__none__">Sem responsável</SelectItem>
          </SelectContent>
        </Select>
        <Select value={pendFilter} onValueChange={setPendFilter}>
          <SelectTrigger className="w-full sm:w-[210px]"><SelectValue placeholder="Pendências" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas as pendências</SelectItem>
            <SelectItem value="sem_plano">Sem plano de pagamento</SelectItem>
            <SelectItem value="sem_horario">Sem horário de treino</SelectItem>
          </SelectContent>
        </Select>
      </div>


      {!loading && students.length > filtered.length && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          <span>
            {students.length - filtered.length} aluno(s) ocultos pelos filtros atuais (de{" "}
            {students.length} cadastrados).
          </span>
          <Button
            size="sm"
            variant="outline"
            className="h-7"
            onClick={() => {
              setSearch("");
              setStatusFilter("all");
              setPaymentFilter("all");
              setCategoryFilter("all");
              setPendFilter("all");
            }}
          >
            Limpar filtros
          </Button>
        </div>
      )}



      {/* Contadores por categoria */}
      {!loading && (
        <div className="flex gap-2 overflow-x-auto pb-1">
          <button
            type="button"
            onClick={() => setCategoryFilter("all")}
            className={`shrink-0 rounded-xl border px-4 py-2 text-left transition-colors ${
              categoryFilter === "all"
                ? "border-primary bg-primary/10"
                : "bg-card hover:bg-muted/50"
            }`}
          >
            <div className="text-xl font-bold leading-none">{baseFiltered.length}</div>
            <div className="text-[11px] text-muted-foreground mt-1">Todos</div>
          </button>
          {categoryCounts.map((c) => (
            <button
              key={c.key}
              type="button"
              onClick={() => setCategoryFilter(categoryFilter === c.key ? "all" : c.key)}
              className={`shrink-0 rounded-xl border px-4 py-2 text-left transition-colors ${
                categoryFilter === c.key
                  ? "border-primary bg-primary/10"
                  : "bg-card hover:bg-muted/50"
              }`}
            >
              <div className="text-xl font-bold leading-none">{c.count}</div>
              <div className="text-[11px] text-muted-foreground mt-1 whitespace-nowrap">{c.label}</div>
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          {[1,2,3,4,5].map((i) => <div key={i} className="h-[250px] bg-muted/50 rounded-xl animate-pulse" />)}
        </div>
      ) : filtered.length === 0 ? (
        <div className="p-12 text-center bg-card rounded-xl border border-dashed">
          <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-50" />
          <h3 className="text-lg font-medium">Nenhum aluno cadastrado</h3>
          <p className="text-muted-foreground mt-1">Clique em "Cadastrar Aluno" para começar.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          {filtered.map((s) => (
            <StudentCard
              key={s.id}
              student={s}
              onEdit={() => openEdit(s)}
              onEditParent={() => openEdit(s, "responsavel")}
              onDelete={() => setRemoving(s)}
              onSuspend={() => setSuspending(s)}
              onWhatsapp={() => openFreeWhatsapp(s)}
              onConfirmEnrollment={() => openConfirmEnrollment(s)}
              onServiceContract={() => setContractStudent(s)}
            />
          ))}

        </div>
      )}

      {modalOpen && (
        <StudentModal
          open={modalOpen}
          tcId={tcId}
          editing={editing}
          initialTab={modalTab}
          parents={parents}
          onClose={() => setModalOpen(false)}
          onSaved={() => { setModalOpen(false); reload(); }}
          save={save}
        />
      )}

      {removing && (
        <RemoveStudentDialog
          open
          studentId={removing.id}
          studentName={removing.name}
          studentStatus={removing.status}
          onClose={() => setRemoving(null)}
          onDone={() => { setRemoving(null); reload(); }}
        />
      )}

      {suspending && (
        <SuspendStudentDialog
          open
          studentId={suspending.id}
          studentName={suspending.name}
          currentFrom={suspending.suspended_from}
          currentUntil={suspending.suspended_until}
          currentReason={suspending.suspension_reason}
          onClose={() => setSuspending(null)}
          onDone={() => { setSuspending(null); reload(); }}
        />
      )}

      {waTarget && (
        <StudentWhatsappDialog
          open
          onOpenChange={(v) => { if (!v) setWaTarget(null); }}
          recipientName={waTarget.student.parents?.name ?? waTarget.student.name}
          recipientPhone={waTarget.student.parents?.phone ?? null}
          defaultMessage={waTarget.message}
          title={waTarget.title}
          allowWithoutPhone={!!waTarget.confirm}
          confirmLabel={waTarget.confirm ? "Confirmar e enviar" : "Enviar"}
          skipSendLabel="Confirmar matrícula"
          onSent={async () => {
            if (waTarget.confirm) {
              try {
                await confirmEnrollment({ data: { studentId: waTarget.student.id } });
                toast.success("Matrícula confirmada.");
              } catch (e: any) {
                toast.error(e?.message || "Erro ao confirmar matrícula.");
              }
              await reload();
            }
          }}
        />
      )}

      <ServiceContractDialog
        open={!!contractStudent}
        student={contractStudent ? { id: contractStudent.id, name: contractStudent.name } : null}
        onClose={() => setContractStudent(null)}
        onSigned={() => reload()}
      />

    </div>

  );
}

function StudentCard({ student, onEdit, onEditParent, onDelete, onSuspend, onWhatsapp, onConfirmEnrollment, onServiceContract }: {
  student: Student; onEdit: () => void; onEditParent: () => void; onDelete: () => void; onSuspend: () => void;
  onWhatsapp: () => void; onConfirmEnrollment: () => void; onServiceContract: () => void;
}) {
  const suspended = isSuspended(student);
  const [signedPhoto, setSignedPhoto] = useState<string | null>(null);
  const viaLink = isLinkStudent(student);
  const pending = isPendingLink(student);
  const confirmed = !!student.enrollment_confirmed_at;
  const contractSigned = !!(student.latest_contract as any)?.parent_signature_url;
  const missing = [!hasPlan(student) ? "plano de pagamento" : null, !hasSchedule(student) ? "horário de treino" : null].filter(Boolean);

  useEffect(() => {
    let cancel = false;
    async function load() {
      if (!student.photo_url) return;
      const { data } = await supabase.storage.from("student_photos").createSignedUrl(student.photo_url, 3600);
      if (!cancel) setSignedPhoto(data?.signedUrl ?? null);
    }
    load();
    return () => { cancel = true; };
  }, [student.photo_url]);

  const payment = student.payment_status ?? "pendente";
  const paymentColors: Record<string, string> = {
    pago: "bg-emerald-500",
    pendente: "bg-amber-500",
    atrasado: "bg-red-500",
  };

  return (
    <Card className={cn("overflow-hidden group", pending && "border-4 border-red-600 shadow-lg shadow-red-600/20")}>
      <div className="relative aspect-square bg-muted">
        <div className="absolute top-2 left-2 flex flex-col gap-1 z-10">
          <Badge className={student.status === "ativo" ? "bg-blue-600" : "bg-gray-500"}>
            {student.status === "ativo" ? "Ativo" : "Inativo"}
          </Badge>
          <Badge className={paymentColors[payment]}>
            {payment.charAt(0).toUpperCase() + payment.slice(1)}
          </Badge>
          {suspended && (
            <Badge className="bg-amber-500">Afastado</Badge>
          )}
          {viaLink && (
            <Badge className={pending ? "bg-red-600" : "bg-slate-600"}>
              {pending ? "Via link • pendente" : "Via link"}
            </Badge>
          )}
        </div>
        <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition z-10">
          <button
            onClick={(e) => { e.stopPropagation(); onWhatsapp(); }}
            className="p-1.5 rounded bg-green-500/95 text-white hover:bg-green-600 shadow"
            title="Enviar mensagem via WhatsApp"
          ><MessageCircle className="w-3.5 h-3.5" /></button>

          <button
            onClick={onSuspend}
            title="Afastamento"
            className={`p-1.5 rounded shadow ${suspended ? "bg-amber-500 text-white hover:bg-amber-600" : "bg-background/90 hover:bg-background"}`}
          ><PauseCircle className="w-3.5 h-3.5" /></button>
          <button onClick={onEdit} className="p-1.5 rounded bg-background/90 hover:bg-background shadow"><Edit className="w-3.5 h-3.5" /></button>
          <button onClick={onDelete} className="p-1.5 rounded bg-background/90 hover:bg-destructive hover:text-destructive-foreground shadow"><Trash2 className="w-3.5 h-3.5" /></button>
        </div>
        {signedPhoto ? (
          <img src={signedPhoto} alt={student.name} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Avatar className="h-20 w-20">
              <AvatarFallback className="bg-muted-foreground/20 text-3xl">
                {student.name.charAt(0).toUpperCase()}
              </AvatarFallback>
            </Avatar>
          </div>
        )}
      </div>
      <CardContent className="p-3 space-y-1">
        <div className="flex items-center justify-between gap-1">
          <p className="font-semibold text-sm line-clamp-1">{student.name}</p>
          {student.jersey_number && (
            <Badge className="text-[10px] px-1.5 py-0 shrink-0" title="Número do atleta">
              #{student.jersey_number}
            </Badge>
          )}
        </div>
        <div className="flex items-center justify-between gap-1">
          <p className="text-xs text-muted-foreground">Nasc.: {fmtDateBr(student.birth_date)}</p>
          {student.category && (
            <Badge variant="outline" className="text-[10px] px-1.5 py-0">{student.category}</Badge>
          )}
        </div>
        {student.latest_contract ? (
          <div className="pt-1 border-t mt-1 space-y-0.5">
            <div className="flex items-center justify-between gap-1">
              <span className="text-[10px] text-muted-foreground">Contrato</span>
              <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                {student.latest_contract.plan_snapshot?.plan_type ?? "—"}
              </Badge>
            </div>
            <p className="text-[10px] text-muted-foreground">
              Vigência: {formatContractPeriod(student.latest_contract.sign_date, student.latest_contract.plan_snapshot?.validity)}
            </p>
          </div>
        ) : student.plan_prices ? (
          <div className="pt-1 border-t mt-1 space-y-0.5">
            <div className="flex items-center justify-between gap-1">
              <span className="text-[10px] text-muted-foreground">Plano</span>
              <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                {student.plan_prices.plan_type ?? "—"}
              </Badge>
            </div>
            <p className="text-[10px] text-muted-foreground">
              Vigência: {student.plan_start_date || student.plan_end_date
                ? `${fmtFullBr(student.plan_start_date)} → ${fmtFullBr(student.plan_end_date)}`
                : "—"}
            </p>
          </div>
        ) : (
          <p className="text-[10px] text-muted-foreground italic pt-1 border-t mt-1">Sem contrato</p>
        )}

        <div className="pt-1 border-t mt-1 space-y-0.5">
          <span className="text-[10px] text-muted-foreground">Turma</span>
          {(student.schedules?.length ?? 0) > 0 ? (
            student.schedules!.map((sc) => (
              <p key={sc.id} className="text-[10px] font-medium leading-tight">
                {sc.name}
              </p>
            ))
          ) : (
            <p className="text-[10px] text-muted-foreground italic">Sem turma</p>
          )}
        </div>


        <div className="pt-2 border-t mt-1 grid grid-cols-2 gap-1">
          <Button
            asChild
            size="sm"
            className={cn(
              "h-8 text-[11px] px-1 text-white",
              hasPlan(student) ? "bg-emerald-600 hover:bg-emerald-700" : "bg-red-600 hover:bg-red-700",
            )}
          >
            <Link to="/m/planos-pagamento">Plano de pagamento</Link>
          </Button>
          <Button
            asChild
            size="sm"
            className={cn(
              "h-8 text-[11px] px-1 text-white",
              hasSchedule(student) ? "bg-emerald-600 hover:bg-emerald-700" : "bg-red-600 hover:bg-red-700",
            )}
          >
            <Link to="/m/horario-treinos">Horário de treino</Link>
          </Button>
        </div>

        <div className="pt-2">
          <Button
            size="sm"
            variant="outline"
            onClick={onEditParent}
            className="w-full h-8 text-[11px] mb-2"
            title={student.parents?.name ? `Responsável: ${student.parents.name}` : "Responsável"}
          >
            <UserCog className="w-3.5 h-3.5 mr-1" />
            {student.parents?.name ? `Resp.: ${student.parents.name}` : "Responsável"}
          </Button>
          <Button
            size="sm"
            onClick={onServiceContract}
            className={cn(
              "w-full h-8 text-[11px] text-white",
              contractSigned ? "bg-emerald-600 hover:bg-emerald-700" : "bg-red-600 hover:bg-red-700",
            )}
          >
            {contractSigned ? "Contrato assinado — visualizar" : "Assinar contrato de prestação de serviço"}
          </Button>
        </div>

        <div className="pt-2 border-t mt-1 space-y-1">
          {!confirmed ? (
            <>
              <Button
                size="sm"
                className="w-full h-8 text-[11px] text-white bg-red-600 hover:bg-red-700"
                onClick={onConfirmEnrollment}
              >

                Confirmar matrícula
              </Button>
              {missing.length > 0 && (
                <p className="text-[10px] text-red-600 leading-tight">
                  Sem {missing.join(" e ")} — ainda assim é possível confirmar a matrícula.
                </p>
              )}
            </>
          ) : (
            <p className="text-[10px] text-emerald-600">Matrícula confirmada</p>
          )}
        </div>
      </CardContent>

    </Card>
  );
}

/* ---------- MODAL ---------- */

function StudentModal({
  open, tcId, editing, initialTab, parents, onClose, onSaved, save,
}: {
  open: boolean;
  tcId: string | null;
  editing: Student | null;
  initialTab?: "aluno" | "responsavel";
  parents: ParentItem[];
  onClose: () => void;
  onSaved: () => void;
  save: ReturnType<typeof useServerFn<typeof upsertStudent>>;
}) {
  const [tab, setTab] = useState<string>(initialTab ?? "aluno");
  const [saving, setSaving] = useState(false);

  // Aluno
  const [name, setName] = useState(editing?.name ?? "");
  const [docType, setDocType] = useState(editing?.document_type ?? "rg");
  const [docFront, setDocFront] = useState<string | null>(editing?.document_front_url ?? null);
  const [docBack, setDocBack] = useState<string | null>(editing?.document_back_url ?? null);
  const [cpf, setCpf] = useState(editing?.cpf ?? "");
  const [birthDate, setBirthDate] = useState(editing?.birth_date ?? "");
  const [category, setCategory] = useState(editing?.category ?? "");
  const [gender, setGender] = useState((editing as any)?.gender || "masculino");

  // Categoria automática a partir do ano de nascimento (documento, CPF ou digitação)
  useEffect(() => {
    if (!birthDate) return;
    const c = categoryFromBirth(birthDate);
    if (c && c !== category) setCategory(c);
  }, [birthDate]);

  const [weight, setWeight] = useState(editing?.weight ? String(editing.weight) : "");
  const [height, setHeight] = useState(editing?.height ? String(editing.height) : "");
  const [status, setStatus] = useState(editing?.status ?? "ativo");
  const [photo, setPhoto] = useState<string | null>(editing?.photo_url ?? null);
  const [numberAlert, setNumberAlert] = useState<string | null>(null);


  // Plano de Saúde
  const [hasHealthPlan, setHasHealthPlan] = useState<boolean>(!!editing?.has_health_plan);
  const [healthPlanName, setHealthPlanName] = useState(editing?.health_plan_name ?? "");
  const [healthPlanContact, setHealthPlanContact] = useState(editing?.health_plan_contact ?? "");




  // OCR de documento via Lovable AI
  const ocr = useServerFn(extractDocument);
  const [ocrLoading, setOcrLoading] = useState(false);
  const runOcr = async (paths: string[]) => {
    if (!paths.length) return;
    setOcrLoading(true);
    try {
      const r = await ocr({ data: { paths } });
      if (r.name && !name.trim()) setName(r.name);
      if (r.cpf) setCpf(r.cpf);
      if (r.birth_date) setBirthDate(r.birth_date);
      if (r.name || r.cpf || r.birth_date) {
        toast.success(
          r.cpf
            ? "Documento lido — campos preenchidos automaticamente."
            : "Documento lido, mas o CPF não foi identificado — preencha manualmente.",
        );
      } else {
        toast.message("Não foi possível extrair dados do documento.");
      }

      // Recorta a foto do documento e usa como foto do atleta
      const docPath = r.photo_path_index != null ? paths[r.photo_path_index] : null;
      if (r.photo_box && docPath && !photo && tcId) {
        const file = await cropPhotoFromDocument(docPath, r.photo_box);
        if (file) {
          const path = `${tcId}/${crypto.randomUUID()}.jpg`;
          const { error } = await supabase.storage
            .from("student_photos")
            .upload(path, file, { contentType: "image/jpeg" });
          if (!error) {
            setPhoto(path);
            toast.success("Foto do documento aplicada como foto do atleta.");
          }
        }
      }

    } catch (e: any) {
      toast.error(e?.message || "Falha ao ler documento.");
    } finally {
      setOcrLoading(false);
    }
  };

  // Responsável
  const [parentId, setParentId] = useState<string | null>(editing?.parents?.id ?? null);
  // Une os dados vindos da lista de responsáveis do CT com os já gravados no
  // próprio aluno (parents/profiles) — garante que dados já preenchidos pelo
  // responsável (ex.: cadastro vindo por link) apareçam automaticamente aqui.
  const initialParent = {
    ...(editing?.parents ?? {}),
    ...(parents.find((p) => p.id === editing?.parents?.id) ?? {}),
  } as Partial<ParentItem>;
  const [parentName, setParentName] = useState(initialParent.name ?? "");
  const [parentCpf, setParentCpf] = useState(initialParent.cpf ?? "");
  const [parentDocType, setParentDocType] = useState<"cpf" | "cnpj">(
    ((initialParent.cpf ?? "").replace(/\D/g, "").length === 14 ? "cnpj" : "cpf"),
  );

  const [parentBirthDate, setParentBirthDate] = useState(initialParent.birth_date ?? "");
  const [parentPhone, setParentPhone] = useState(initialParent.phone ?? "");
  const [parentEmail, setParentEmail] = useState(initialParent.email ?? "");
  const [parentCep, setParentCep] = useState(initialParent.cep ?? "");
  const [parentStreet, setParentStreet] = useState(initialParent.street ?? "");
  const [parentNumber, setParentNumber] = useState(initialParent.number ?? "");
  const [parentComplement, setParentComplement] = useState(initialParent.complement ?? "");
  const [parentNeighborhood, setParentNeighborhood] = useState(initialParent.neighborhood ?? "");
  const [parentCity, setParentCity] = useState(initialParent.city ?? "");
  const [parentState, setParentState] = useState(initialParent.state ?? "");
  const [parentCepLoading, setParentCepLoading] = useState(false);

  // Trava: se o responsável já tem conta ativa, o CT NÃO edita dados pessoais/acesso
  const parentOwnsAccount = !!parents.find((p) => p.id === parentId)?.user_id;
  const studentNameLocked = !!(editing as any)?.name_locked_by_parent;
  const studentPersonalLocked = parentOwnsAccount;


  // Currículo
  const [youtube, setYoutube] = useState(editing?.youtube_url ?? "");
  const [tournaments, setTournaments] = useState<Tournament[]>(editing?.tournaments ?? []);

  // Skin
  const [skin, setSkin] = useState<any>(editing?.avatar_config ?? {
    pose: "arms-crossed",
    skinTone: "#f1c27d",
    hairStyle: "slicked",
    hairColor: "#3e2723",
    facialHair: "beard",
    facialHairColor: "#3e2723",
    shirtColor: "#dc2626",
    shortsColor: "#ffffff",
    socksColor: "#dc2626",
    shirtNumber: "10",
    shirtName: "",
  });
  const [jerseyNumber, setJerseyNumber] = useState(editing?.jersey_number ?? skin.shirtNumber ?? "");
  const numberConfigFn = useServerFn(getMyAthleteNumberConfig);
  const [numberConfig, setNumberConfig] = useState<AthleteNumberConfig>(DEFAULT_ATHLETE_NUMBER_CONFIG);
  const [usedNumbers, setUsedNumbers] = useState<string[]>([]);
  useEffect(() => {
    let alive = true;
    numberConfigFn({} as any)
      .then((c: any) => {
        if (!alive || !c) return;
        setNumberConfig({ digits: c.digits, allowRepeated: c.allowRepeated });
        setUsedNumbers(Array.isArray(c.used) ? c.used : []);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);
  const numberOptions = useMemo(() => {
    const usedSet = new Set(
      usedNumbers
        .filter((u) => u !== (editing?.jersey_number ?? ""))
        .map((u) => String(Number(String(u).replace(/\D/g, "") || "-1"))),
    );
    const q = String(jerseyNumber ?? "").replace(/\D/g, "");
    const max = Math.pow(10, numberConfig.digits) - 1;
    const out: { value: string; used: boolean }[] = [];
    for (let n = 0; n <= max && out.length < 18; n++) {
      const s = String(n);
      if (q && !s.includes(q)) continue;
      const used = !numberConfig.allowRepeated && usedSet.has(s);
      out.push({ value: s, used });
    }
    return out;
  }, [jerseyNumber, numberConfig, usedNumbers, editing?.jersey_number]);

  const numberValidation = useMemo(() => {
    if (!jerseyNumber) return { status: "idle" as const, message: null as string | null };
    const check = validateAthleteNumber(jerseyNumber, numberConfig.digits);
    if (check.error) return { status: "error" as const, message: check.error };
    if (
      !numberConfig.allowRepeated &&
      usedNumbers.filter((u) => u !== (editing?.jersey_number ?? "")).includes(check.value ?? "")
    ) {
      return { status: "error" as const, message: "Este número já está em uso por outro atleta." };
    }
    return { status: "ok" as const, message: null };
  }, [jerseyNumber, numberConfig, usedNumbers, editing?.jersey_number]);
  const [jerseyName, setJerseyName] = useState(editing?.jersey_name ?? skin.shirtName ?? "");


  const onPickParent = (id: string) => {
    if (id === "__new") {
      setParentId(null);
      setParentName(""); setParentCpf(""); setParentBirthDate(""); setParentPhone(""); setParentEmail("");
      setParentCep(""); setParentStreet(""); setParentNumber(""); setParentComplement("");
      setParentNeighborhood(""); setParentCity(""); setParentState("");
      return;
    }
    const p = parents.find((x) => x.id === id);
    if (!p) return;
    setParentId(p.id);
    setParentName(p.name);
    setParentCpf(p.cpf ?? "");
    setParentBirthDate((p as any).birth_date ?? "");
    setParentPhone(p.phone ?? "");
    setParentEmail(p.email ?? "");
    setParentCep(p.cep ?? "");
    setParentStreet(p.street ?? "");
    setParentNumber(p.number ?? "");
    setParentComplement(p.complement ?? "");
    setParentNeighborhood(p.neighborhood ?? "");
    setParentCity(p.city ?? "");
    setParentState(p.state ?? "");
  };

  const fetchParentCep = async (v: string) => {
    setParentCepLoading(true);
    try {
      const r = await lookupCep(v);
      if (!r) return;
      if (r.logradouro) setParentStreet(r.logradouro);
      if (r.bairro) setParentNeighborhood(r.bairro);
      if (r.localidade) setParentCity(r.localidade);
      if (r.uf) setParentState(r.uf);
    } finally {
      setParentCepLoading(false);
    }
  };

  const addTournament = () => setTournaments([...tournaments, { name: "", position: "", notes: "" }]);
  const removeTournament = (i: number) => setTournaments(tournaments.filter((_, idx) => idx !== i));
  const updateTournament = (i: number, patch: Partial<Tournament>) =>
    setTournaments(tournaments.map((t, idx) => (idx === i ? { ...t, ...patch } : t)));

  const onSubmit = async () => {
    if (!name.trim()) { toast.error("Nome do aluno é obrigatório"); setTab("aluno"); return; }
    if (!parentName.trim()) { toast.error("Nome do responsável é obrigatório"); setTab("responsavel"); return; }
    if (parentDocType === "cnpj") {
      if ((parentCpf || "").replace(/\D/g, "").length === 0) {
        toast.error("CNPJ do responsável é obrigatório.");
        setTab("responsavel"); return;
      }
    } else if (!isValidCpf(parentCpf)) {
      toast.error("CPF do responsável é obrigatório e deve ter 11 dígitos válidos.");
      setTab("responsavel"); return;
    }

    const phoneDigits = (parentPhone || "").replace(/\D/g, "");
    if (phoneDigits.length < 10 || phoneDigits.length > 11) {
      toast.error("Telefone do responsável é obrigatório: DDD + 8 ou 9 dígitos.");
      setTab("responsavel"); return;
    }
    if (!parentBirthDate || !/^\d{4}-\d{2}-\d{2}$/.test(parentBirthDate)) {
      toast.error("Data de nascimento do responsável é obrigatória (usada para o responsável ativar a conta).");
      setTab("responsavel"); return;
    }
    const numCheck = validateAthleteNumber(jerseyNumber, numberConfig.digits);
    if (numCheck.error) { toast.error(numCheck.error); setTab("aluno"); return; }
    if (numberValidation.status === "error") {
      setTab("aluno");
      setNumberAlert(numberValidation.message ?? "Número do atleta inválido.");
      return;
    }

    setSaving(true);

    try {
      const result = await save({
        data: {
          id: editing?.id ?? null,
          name,
          cpf: cpf || null,
          birth_date: birthDate || null,
          category: category || null,
          gender: gender || null,

          weight: weight ? Number(weight) : null,
          height: height ? Number(height) : null,
          status,
          document_type: docType,
          document_front_url: docFront,
          document_back_url: docBack,
          photo_url: photo,
          youtube_url: youtube || null,
          tournaments,
          avatar_config: skin,
          jersey_number: jerseyNumber || null,
          jersey_name: jerseyName || null,
          payment_status: editing?.payment_status ?? "pendente",
          has_health_plan: hasHealthPlan,
          health_plan_name: hasHealthPlan ? healthPlanName || null : null,
          health_plan_contact: hasHealthPlan ? healthPlanContact || null : null,
          parent: {
            id: parentId,
            name: parentName,
            cpf: (parentDocType === "cnpj" ? padCnpj(parentCpf) : parentCpf) || null,
            birth_date: parentBirthDate || null,
            phone: parentPhone || null,
            email: parentEmail || null,
            cep: parentCep || null,
            street: parentStreet || null,
            number: parentNumber || null,
            complement: parentComplement || null,
            neighborhood: parentNeighborhood || null,
            city: parentCity || null,
            state: parentState || null,
          },
        },
      });
      toast.success(editing ? "Aluno atualizado" : "Aluno cadastrado");
      onSaved();

    } catch (e: any) {
      const message = e?.message || "Erro ao salvar";
      if (/já está em uso/i.test(message)) {
        setTab("aluno");
        setNumberAlert(message);
      } else {
        toast.error(message);
      }

    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editing ? "Editar Aluno" : "Cadastrar Novo Aluno"}</DialogTitle>
          <DialogDescription>Preencha os dados do aluno.</DialogDescription>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid grid-cols-4 w-full">
            <TabsTrigger value="aluno">Aluno</TabsTrigger>
            <TabsTrigger value="responsavel">Responsável</TabsTrigger>
            <TabsTrigger value="curriculo">Currículo</TabsTrigger>
            <TabsTrigger value="skin">Skin</TabsTrigger>
          </TabsList>

          {/* ---- ALUNO ---- */}
          <TabsContent value="aluno" className="space-y-4 pt-4">
            <Card className="border-dashed border-primary/40 bg-primary/5">
              <CardContent className="p-4 space-y-3">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <ScanLine className="w-4 h-4 text-primary" />
                  Captura Inteligente de Documento (Recomendado)
                </div>
                <p className="text-xs text-muted-foreground">
                  Tire uma foto do documento para preencher automaticamente Nome, CPF e Data de Nascimento.
                </p>
                <div className="space-y-2">
                  <Label className="text-xs">Tipo de Documento</Label>
                  <Select value={docType} onValueChange={setDocType}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="rg">Carteira de Identidade (RG)</SelectItem>
                      <SelectItem value="cpf">CPF</SelectItem>
                      <SelectItem value="cnh">CNH</SelectItem>
                      <SelectItem value="certidao">Certidão de Nascimento</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <StudentPhotoUpload
                    tcId={tcId} value={docFront} label="Frente" accent
                    onChange={(p) => { setDocFront(p); if (p) runOcr([p, docBack].filter(Boolean) as string[]); }}
                  />
                  <StudentPhotoUpload
                    tcId={tcId} value={docBack} label="Verso" accent
                    onChange={(p) => { setDocBack(p); if (p && docFront) runOcr([docFront, p]); }}
                  />
                </div>
                {ocrLoading && (
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Loader2 className="w-3 h-3 animate-spin" />
                    Lendo documento com IA...
                  </div>
                )}
              </CardContent>
            </Card>

            {(studentPersonalLocked || studentNameLocked) && (
              <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
                <strong>Dados gerenciados pelo Usuário.</strong>{" "}
                {studentNameLocked && "O nome do aluno "}
                {studentNameLocked && studentPersonalLocked && "e "}
                {studentPersonalLocked && "CPF, nascimento, foto e documentos "}
                são editados pelo responsável no portal e não podem ser alterados aqui.
              </div>
            )}

            <CpfLookupField
              cpf={cpf}
              setCpf={setCpf}
              onResolved={({ name: apiName, birth_date, category: cat }) => {
                if (apiName && !name.trim()) setName(apiName);
                if (birth_date) setBirthDate(birth_date);
                if (cat) setCategory(cat);
              }}
              disabled={studentPersonalLocked && !!cpf}
            />

            <DebtWarning cpf={cpf} />


            <div className="space-y-2">
              <Label>Nome Completo</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Nome do aluno" disabled={studentNameLocked && !!name.trim()} />
            </div>


            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-2">
                <Label>Data de Nasc.</Label>
                <Input type="date" value={birthDate ?? ""} onChange={(e) => setBirthDate(e.target.value)} disabled={studentPersonalLocked && !!birthDate} />
              </div>
              <div className="space-y-2">
                <Label>Ano de Nasc.</Label>
                <Input value={birthDate ? birthDate.slice(0, 4) : ""} disabled />
              </div>
              <div className="space-y-2">
                <Label>Categoria</Label>
                <Input value={category} onChange={(e) => setCategory(e.target.value)} placeholder="Sub-15" />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Sexo</Label>
              <select
                className="w-full h-10 rounded-md border bg-background px-3 text-sm"
                value={gender}
                onChange={(e) => setGender(e.target.value)}
              >
                <option value="masculino">Masculino</option>
                <option value="feminino">Feminino</option>
              </select>
            </div>



            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Peso (kg)</Label>
                <Input type="number" step="0.1" value={weight} onChange={(e) => setWeight(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>Altura (cm)</Label>
                <Input type="number" step="0.01" value={height} onChange={(e) => setHeight(e.target.value)} />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Número do Atleta (opcional)</Label>
              <Input
                inputMode="numeric"
                maxLength={numberConfig.digits}
                value={jerseyNumber}
                onChange={(e) => setJerseyNumber(e.target.value.replace(/\D/g, "").slice(0, numberConfig.digits))}
                onBlur={() => {
                  if (numberValidation.status === "error") {
                    setNumberAlert(numberValidation.message ?? "Número do atleta inválido.");
                  }
                }}
                placeholder={`até ${numberConfig.digits} algarismos`}
                className={cn(
                  numberValidation.status === "error" && "border-destructive text-destructive focus-visible:ring-destructive",
                  numberValidation.status === "ok" && "border-green-500 text-green-700 focus-visible:ring-green-500",
                )}
              />
              {numberValidation.message && (
                <p role="alert" className="text-xs text-destructive">{numberValidation.message}</p>
              )}
              {numberOptions.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  <span className="text-xs text-muted-foreground self-center">Números:</span>
                  {numberOptions.map((o) => (
                    <button
                      key={o.value}
                      type="button"
                      disabled={o.used}
                      title={o.used ? "Número já utilizado por outro atleta" : "Número disponível"}
                      onClick={() => !o.used && setJerseyNumber(o.value)}
                      className={cn(
                        "rounded-md border px-2 py-0.5 text-xs transition",
                        o.used
                          ? "border-destructive/60 bg-destructive/10 text-destructive line-through cursor-not-allowed opacity-70"
                          : "border-green-500 bg-green-50 text-green-700 hover:bg-green-100",
                      )}
                    >
                      {o.value}
                    </button>
                  ))}
                </div>
              )}

              <p className="text-xs text-muted-foreground">
                Até {numberConfig.digits} algarismos
                {numberConfig.allowRepeated ? " • números repetidos permitidos" : " • número único por atleta"}
                . O mesmo número vale para Cadastro, Uniforme e Skin.
              </p>
            </div>


            <div className="space-y-2">
              <Label>Situação</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ativo">Ativo</SelectItem>
                  <SelectItem value="inativo">Inativo</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Foto do Aluno</Label>
              <StudentPhotoUpload tcId={tcId} value={photo} onChange={setPhoto} label="Enviar foto do rosto" tall accent="green" />
            </div>

            {/* ---- PLANO DE SAÚDE ---- */}
            <Card className="border-dashed">
              <CardContent className="p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm font-semibold">
                    <Heart className="w-4 h-4 text-rose-500" />
                    Plano de Saúde
                  </div>
                  <div className="flex items-center gap-2">
                    <Label htmlFor="hp-switch" className="text-xs text-muted-foreground">Possui plano</Label>
                    <Switch id="hp-switch" checked={hasHealthPlan} onCheckedChange={setHasHealthPlan} />
                  </div>
                </div>
                {hasHealthPlan && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label>Nome do Plano</Label>
                      <Input value={healthPlanName} onChange={(e) => setHealthPlanName(e.target.value)} placeholder="Ex: Unimed" />
                    </div>
                    <div className="space-y-2">
                      <Label>Contato do Plano</Label>
                      <Input value={healthPlanContact} onChange={(e) => setHealthPlanContact(e.target.value)} placeholder="Telefone / 0800" />
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

          </TabsContent>


          {/* ---- RESPONSÁVEL ---- */}
          <TabsContent value="responsavel" className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label>Responsável Vinculado</Label>
              <Select value={parentId ?? "__new"} onValueChange={onPickParent}>
                <SelectTrigger><SelectValue placeholder="Selecione um responsável" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__new">+ Novo responsável</SelectItem>
                  {parents.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {parentOwnsAccount && (
              <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
                <strong>Responsável com conta de acesso.</strong> O Centro de Treinamento
                pode editar nome, CPF, telefone, data de nascimento e endereço. Apenas o
                <strong> e-mail</strong> não pode ser alterado, pois é a credencial de login.
              </div>
            )}

            {!parentOwnsAccount && parentId && (
              <ActivateAccessSection
                parentId={parentId}
                parentName={parentName}
                parentPhone={parentPhone}
              />
            )}


            <div className="flex flex-wrap items-center justify-between gap-2">
              <Label className="text-sm font-semibold">Dados do Responsável</Label>
              <div className="flex items-center gap-2">
                <div className="flex overflow-hidden rounded-md border">
                  <button
                    type="button"
                    onClick={() => setParentDocType("cpf")}
                    className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wide ${parentDocType === "cpf" ? "bg-primary text-primary-foreground" : "bg-background text-muted-foreground"}`}
                  >
                    CPF
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setParentDocType("cnpj");
                      if (!parentBirthDate) setParentBirthDate("2000-01-01");
                    }}
                    className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wide ${parentDocType === "cnpj" ? "bg-primary text-primary-foreground" : "bg-background text-muted-foreground"}`}
                  >
                    CNPJ
                  </button>
                </div>
                <Button type="button" variant="secondary" size="sm" className="bg-amber-400 hover:bg-amber-500 text-amber-950" onClick={() => onPickParent("__new")}>
                  <UserPlus2 className="w-4 h-4 mr-1" /> Adicionar outro aluno
                </Button>
              </div>
            </div>


            <div className="space-y-2">
              <Label>Nome <span className="text-destructive">*</span></Label>
              <Input value={parentName} onChange={(e) => setParentName(e.target.value)} required />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>
                  {parentDocType === "cnpj" ? "CNPJ" : "CPF"} <span className="text-destructive">*</span>
                </Label>
                <Input
                  value={parentCpf}
                  onChange={(e) =>
                    setParentCpf(parentDocType === "cnpj" ? maskCNPJ(e.target.value) : maskCpf(e.target.value))
                  }
                  onBlur={() => {
                    if (parentDocType === "cnpj" && parentCpf.replace(/\D/g, "").length > 0) {
                      setParentCpf(maskCNPJ(padCnpj(parentCpf)));
                    }
                  }}
                  placeholder={parentDocType === "cnpj" ? "00.000.000/0000-00" : "000.000.000-00"}
                  inputMode="numeric"
                  required

                />

              </div>
              <div className="space-y-2">
                <Label>Telefone <span className="text-destructive">*</span></Label>
                <Input
                  value={parentPhone}
                  onChange={(e) => setParentPhone(maskPhone(e.target.value))}
                  placeholder="(00) 00000-0000"
                  inputMode="tel"
                  required

                />
              </div>
            </div>

            <DebtWarning cpf={parentCpf} />


            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>
                  {parentDocType === "cnpj" ? "Data de fundação" : "Data de nascimento"}{" "}
                  <span className="text-destructive">*</span>
                </Label>
                <Input
                  type="date"
                  value={parentBirthDate ?? ""}
                  onChange={(e) => setParentBirthDate(e.target.value)}
                  required

                />
                <p className="text-xs text-muted-foreground">
                  {parentDocType === "cnpj"
                    ? "Usada pelo responsável para ativar o acesso à ficha via CNPJ + data de fundação (padrão 01/01/2000)."
                    : "Usada pelo responsável para ativar o acesso à ficha via CPF + data de nascimento."}
                </p>
              </div>
              <div className="space-y-2">
                <Label>E-mail</Label>
                <Input type="email" value={parentEmail} onChange={(e) => setParentEmail(e.target.value)} disabled={parentOwnsAccount && !!parentEmail.trim()} />
              </div>
            </div>



            <div className="space-y-2">
              <Label>CEP</Label>
              <Input
                value={parentCep}
                onChange={(e) => setParentCep(maskCep(e.target.value))}
                onBlur={(e) => fetchParentCep(e.target.value)}
                placeholder="00000-000"
              />
              {parentCepLoading && <p className="text-xs text-muted-foreground">Buscando endereço...</p>}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="space-y-2 sm:col-span-2">
                <Label>Rua</Label>
                <Input value={parentStreet} onChange={(e) => setParentStreet(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>Número</Label>
                <Input value={parentNumber} onChange={(e) => setParentNumber(e.target.value)} />
              </div>
              <div className="space-y-2 sm:col-span-3">
                <Label>Complemento</Label>
                <Input value={parentComplement} onChange={(e) => setParentComplement(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>Bairro</Label>
                <Input value={parentNeighborhood} onChange={(e) => setParentNeighborhood(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>Cidade</Label>
                <Input value={parentCity} onChange={(e) => setParentCity(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>Estado (UF)</Label>
                <Input value={parentState} maxLength={2} onChange={(e) => setParentState(e.target.value.toUpperCase())} />
              </div>
            </div>
          </TabsContent>

          {/* ---- CURRÍCULO ---- */}
          <TabsContent value="curriculo" className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label>Vídeo de Destaque (Link do YouTube)</Label>
              <Input value={youtube} onChange={(e) => setYoutube(e.target.value)} placeholder="https://www.youtube.com/watch?v=..." />
              <p className="text-xs text-muted-foreground">
                Cole o link completo do vídeo no YouTube. Recomendamos vídeos curtos mostrando as melhores jogadas do atleta.
              </p>
            </div>

            <div className="border-t pt-4">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <Label className="text-sm font-semibold">Histórico de Campeonatos</Label>
                  <p className="text-xs text-muted-foreground">Registre os torneios, prêmios ou posições de destaque.</p>
                </div>
                <Button type="button" size="sm" className="bg-amber-400 hover:bg-amber-500 text-amber-950" onClick={addTournament}>
                  <Plus className="w-4 h-4 mr-1" /> Adicionar
                </Button>
              </div>

              {tournaments.length === 0 ? (
                <div className="border border-dashed rounded-lg p-6 text-center text-sm text-muted-foreground">
                  Nenhum campeonato registrado no currículo.
                </div>
              ) : (
                <div className="space-y-2">
                  {tournaments.map((t, i) => (
                    <div key={i} className="border rounded-lg p-3 space-y-2">
                      <div className="flex items-start gap-2">
                        <Input value={t.name} onChange={(e) => updateTournament(i, { name: e.target.value })} placeholder="Nome do campeonato" className="flex-1" />
                        <Button type="button" variant="ghost" size="icon" onClick={() => removeTournament(i)}><X className="w-4 h-4" /></Button>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <Input value={t.position ?? ""} onChange={(e) => updateTournament(i, { position: e.target.value })} placeholder="Posição/Prêmio" />
                        <Input value={t.notes ?? ""} onChange={(e) => updateTournament(i, { notes: e.target.value })} placeholder="Observações" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* ---- SKIN ---- */}
          <TabsContent value="skin" className="space-y-4 pt-4">
            <SkinEditor
              value={skin}
              onChange={setSkin}
              jerseyNumber={jerseyNumber}
              jerseyName={jerseyName}
              onJerseyNumberChange={setJerseyNumber}
              onJerseyNameChange={setJerseyName}
              hint="Salve o aluno para acessar as opções de Gamificação (FutBux)."
            />
          </TabsContent>

        </Tabs>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          {tab !== "skin" ? (
            <Button onClick={() => {
              if (tab === "aluno" && numberValidation.status === "error") {
                setNumberAlert(numberValidation.message ?? "Número do atleta inválido.");
                return;
              }
              const next = tab === "aluno" ? "responsavel" : tab === "responsavel" ? "curriculo" : "skin";
              setTab(next);
            }}>
              Continuar <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button onClick={onSubmit} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Salvar Cadastro
            </Button>
          )}
        </DialogFooter>
      </DialogContent>

      <AlertDialog open={!!numberAlert} onOpenChange={(o) => !o && setNumberAlert(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Número já utilizado</AlertDialogTitle>
            <AlertDialogDescription>
              {numberAlert} Escolha outro número para o atleta.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setNumberAlert(null)}>OK</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );

}

/* ---------- Photo upload ---------- */

function StudentPhotoUpload({
  tcId, value, onChange, label, tall, accent,
}: {
  tcId: string | null;
  value: string | null;
  onChange: (path: string | null) => void;
  label: string;
  tall?: boolean;
  accent?: boolean | "primary" | "green";
}) {
  const [uploading, setUploading] = useState(false);
  const [rotating, setRotating] = useState(false);
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    let cancel = false;
    async function load() {
      if (!value) { setUrl(null); return; }
      const { data } = await supabase.storage.from("student_photos").createSignedUrl(value, 3600);
      if (!cancel) setUrl(data?.signedUrl ?? null);
    }
    load();
    return () => { cancel = true; };
  }, [value]);

  const handleRotate = async () => {
    if (!value || !url) return;
    setRotating(true);
    try {
      const rotatedBlob = await rotateImageFromUrl(url);
      const { error } = await supabase.storage
        .from("student_photos")
        .upload(value, rotatedBlob, { contentType: "image/jpeg", upsert: true });
      if (error) throw error;
      setUrl(URL.createObjectURL(rotatedBlob));
    } catch (e: any) {
      toast.error(e?.message || "Erro ao girar imagem");
    } finally {
      setRotating(false);
    }
  };

  const handleFile = async (file: File) => {
    if (!tcId) { toast.error("Cadastre o CT primeiro"); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("Arquivo muito grande (máx 5MB)"); return; }
    setUploading(true);
    try {
      const ext = file.name.split(".").pop() || "jpg";
      const path = `${tcId}/${crypto.randomUUID()}.${ext}`;
      const { error } = await supabase.storage.from("student_photos").upload(path, file, { contentType: file.type });
      if (error) throw error;
      if (value) await supabase.storage.from("student_photos").remove([value]);
      onChange(path);
    } catch (e: any) {
      toast.error(e?.message || "Erro no upload");
    } finally {
      setUploading(false);
    }
  };

  const height = tall ? "h-32" : "h-24";
  const variant = accent === "green" ? "green" : accent ? "primary" : "muted";
  const placeholderClass =
    variant === "green" ? "border-green-500 bg-green-50 hover:bg-green-100"
    : variant === "primary" ? "border-primary/50 bg-primary/10 hover:bg-primary/15"
    : "border-dashed bg-muted/20 hover:bg-muted/40";
  const iconClass = variant === "green" ? "text-green-700" : variant === "primary" ? "text-primary" : "text-muted-foreground";
  const textClass = variant === "green" ? "text-green-800 font-medium" : variant === "primary" ? "text-primary font-medium" : "text-muted-foreground";

  return (
    <div>
      {url ? (
        <div className={`relative ${height} w-full rounded-lg border bg-muted/30 overflow-hidden group`}>
          <img src={url} alt={label} className="w-full h-full object-cover" />
          <button
            type="button"
            onClick={handleRotate}
            disabled={rotating}
            title="Girar foto"
            className="absolute top-1 left-1 p-1 rounded-full bg-background/90 border shadow-sm text-foreground transition"
          >
            {rotating ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCw className="w-3 h-3" />}
          </button>
          <button type="button" onClick={() => onChange(null)} className="absolute top-1 right-1 p-1 rounded-full bg-destructive text-destructive-foreground opacity-0 group-hover:opacity-100 transition">
            <X className="w-3 h-3" />
          </button>
        </div>
      ) : (
        <label className={`flex flex-col items-center justify-center ${height} w-full rounded-lg border border-dashed cursor-pointer transition ${placeholderClass}`}>
          {uploading ? (
            <Loader2 className={`w-5 h-5 animate-spin ${iconClass}`} />
          ) : (
            <>
              <Upload className={`w-4 h-4 mb-1 ${iconClass}`} />
              <span className={`text-xs ${textClass}`}>{label}</span>
            </>
          )}
          <input type="file" accept="image/*" className="hidden" disabled={uploading || !tcId}
            onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); e.target.value = ""; }} />
        </label>
      )}
    </div>
  );
}

function formatCpfMask(v: string) {
  const d = v.replace(/\D/g, "").slice(0, 11);
  return d
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d{1,2})$/, "$1-$2");
}

function CpfLookupField({
  cpf,
  setCpf,
  disabled,
}: {
  cpf: string;
  setCpf: (v: string) => void;
  onResolved?: (r: { name: string; birth_date: string | null; year: string | null; category: string | null }) => void;
  disabled?: boolean;
}) {
  return (
    <div className="space-y-2">
      <Label>CPF</Label>
      <Input
        value={cpf}
        onChange={(e) => setCpf(formatCpfMask(e.target.value))}
        placeholder="000.000.000-00"
        inputMode="numeric"
        className="border-primary/60 ring-1 ring-primary/30 focus-visible:ring-primary bg-primary/5 font-medium"
        disabled={disabled}
      />

      <p className="text-xs text-muted-foreground">
        Digite o CPF do aluno.
      </p>
    </div>
  );
}

function ActivateAccessSection({
  parentId, parentName, parentPhone,
}: { parentId: string; parentName: string; parentPhone: string }) {
  const createInv = useServerFn(createParentInvite);
  const sendWa = useServerFn(sendInviteWhatsapp);
  const [creating, setCreating] = useState(false);
  const [sending, setSending] = useState(false);
  const [invite, setInvite] = useState<{ id: string; token: string } | null>(null);

  const link = invite ? `${window.location.origin}/convite/${invite.token}` : "";

  const generate = async () => {
    const digits = (parentPhone || "").replace(/\D/g, "");
    if (!parentName.trim()) { toast.error("Preencha o nome do responsável antes."); return; }
    if (digits.length < 10) { toast.error("Preencha o telefone do responsável antes."); return; }
    setCreating(true);
    try {
      const r = await createInv({ data: {
        recipientName: parentName.trim(),
        recipientPhone: parentPhone,
        targetParentId: parentId,
        maxUses: 1,
      }});
      setInvite({ id: (r as any).id, token: (r as any).token });
      toast.success("Link de ativação gerado.");
    } catch (e: any) {
      toast.error(e?.message ?? "Erro ao gerar link");
    } finally { setCreating(false); }
  };

  const copy = async () => {
    if (!link) return;
    await navigator.clipboard.writeText(link);
    toast.success("Link copiado.");
  };

  const sendApi = async () => {
    if (!invite) return;
    setSending(true);
    try {
      await sendWa({ data: { id: invite.id, origin: window.location.origin } });
      toast.success("Convite enviado por WhatsApp.");
    } catch (e: any) {
      toast.error(e?.message ?? "Erro ao enviar");
    } finally { setSending(false); }
  };

  const sendWeb = () => {
    if (!link) return;
    const digits = (parentPhone || "").replace(/\D/g, "");
    const phone = digits.length <= 11 ? `55${digits}` : digits;
    const text = encodeURIComponent(
      `Olá ${parentName}, ative seu acesso ao portal do CT: ${link}`
    );
    window.open(`https://wa.me/${phone}?text=${text}`, "_blank");
  };

  return (
    <div className="rounded-md border border-blue-200 bg-blue-50/60 p-3 space-y-2">
      <div className="flex items-center gap-2 text-sm text-blue-900">
        <KeyRound className="h-4 w-4" />
        <strong>Ativação de acesso ao portal</strong>
      </div>
      <p className="text-xs text-blue-900/80">
        Este responsável ainda não tem login. Gere um link para ele criar
        e-mail e senha — a ficha atual, alunos e histórico são preservados.
      </p>

      {!invite ? (
        <Button size="sm" onClick={generate} disabled={creating}>
          {creating ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <KeyRound className="h-4 w-4 mr-1" />}
          Gerar link de ativação
        </Button>
      ) : (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Input readOnly value={link} className="text-xs" />
            <Button type="button" size="sm" variant="outline" onClick={copy}>
              <Copy className="h-4 w-4" />
            </Button>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button type="button" size="sm" onClick={sendApi} disabled={sending}>
              {sending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />}
              Enviar via API
            </Button>
            <Button type="button" size="sm" variant="secondary" onClick={sendWeb}>
              <MessageCircle className="h-4 w-4 mr-1" />
              WhatsApp Web
            </Button>
          </div>
          <p className="text-[11px] text-blue-900/70">
            O link é de uso único e expira automaticamente.
          </p>
        </div>
      )}
    </div>
  );
}



